import { z } from 'zod/v4';

/**
 * Lifecycle status of a single amfori sync run, persisted in
 * `amfori_sync_history.status`.
 *
 * - `RUNNING` — the only non-terminal state; minted by `startSync`.
 * - `COMPLETED` — every phase finished without error.
 * - `PARTIAL` — at least one phase failed but the run still produced data.
 * - `FAILED` — the run could not complete (hard failure / master callback).
 *
 * Exactly one `RUNNING → terminal` transition is allowed per run; the
 * orchestrator enforces this via a rows-affected guard.
 */
export const AmforiSyncStatusSchema = z.enum([
  'RUNNING',
  'COMPLETED',
  'PARTIAL',
  'FAILED',
]);

export type AmforiSyncStatus = z.infer<typeof AmforiSyncStatusSchema>;

/** The three terminal states a sync run can settle into. */
export const AmforiSyncTerminalStatusSchema = z.enum([
  'COMPLETED',
  'PARTIAL',
  'FAILED',
]);

export type AmforiSyncTerminalStatus = z.infer<
  typeof AmforiSyncTerminalStatusSchema
>;

/**
 * What triggered a sync run, persisted in `amfori_sync_history.trigger_source`.
 *
 * - `INITIAL` — the brand's FIRST successful credentials save auto-enqueued the
 *   sync. Fired exactly once per connection; rotations/re-verifications do not
 *   re-trigger.
 * - `MANUAL` — a user pressed "sync now" in the UI.
 * - `SCHEDULED` — a recurring background schedule fired the sync.
 */
export const AmforiSyncTriggerSourceSchema = z.enum([
  'INITIAL',
  'MANUAL',
  'SCHEDULED',
]);

export type AmforiSyncTriggerSource = z.infer<
  typeof AmforiSyncTriggerSourceSchema
>;
